import React from 'react';
import { useSelector } from 'react-redux';

import MetricCard, { MetricItem, VariabilityStatusPill } from 'components/FinancialCheckup/MetricCard/MetricCard';
import { getFinancialMetricsState } from 'selectors/financialMetrics';
import { PeriodMetrics } from 'api/FinancialMetricsApi';
import { formatMonetaryAmount } from 'utils/formatMonetaryAmount';
import {
  getIncomeVariabilityStatus,
  getExpenseVariabilityStatus,
} from 'components/FinancialCheckup/FinancialDashboard/utils';

import InsightSection from 'components/FinancialCheckup/FinancialDashboard/InsightSection';

import styles from './FinancialMetricsSection.module.scss';

const createMetricItems = (metrics: PeriodMetrics | null): MetricItem[] | null => {
  if (!metrics || !metrics.mtdValue || !metrics.ytdValue) return null;
  return [
    {
      label: 'Month to date',
      value: formatMonetaryAmount(metrics.mtdValue, true),
    },
    {
      label: 'Year to date',
      value: formatMonetaryAmount(metrics.ytdValue, true),
    },
  ];
};

const FinancialMetricsSection: React.FC = () => {
  const { data: financialMetricsData } = useSelector(getFinancialMetricsState);

  if (!financialMetricsData) return null;

  const { income, spending, ivi, evi } = financialMetricsData;

  const incomeItems = createMetricItems(income);
  const expenseItems = createMetricItems(spending);

  const incomeVariabilityStatus = typeof ivi === 'number' ? getIncomeVariabilityStatus(ivi) : undefined;
  const expenseVariabilityStatus = typeof evi === 'number' ? getExpenseVariabilityStatus(evi) : undefined;

  return (
    <div className={styles.container}>
      {incomeItems && (
        <div className={styles.section}>
          <div className={styles.titleRow}>
            <p className={styles.sectionLabel}>Income</p>
            {incomeVariabilityStatus && <VariabilityStatusPill status={incomeVariabilityStatus} />}
          </div>
          <MetricCard items={incomeItems} />
        </div>
      )}
      {expenseItems && (
        <div className={styles.section}>
          <div className={styles.titleRow}>
            <p className={styles.sectionLabel}>Expenses</p>
            {expenseVariabilityStatus && <VariabilityStatusPill status={expenseVariabilityStatus} />}
          </div>
          <MetricCard items={expenseItems} />
        </div>
      )}
      <InsightSection />
    </div>
  );
};

export default FinancialMetricsSection;
